'use client';

import './donate-form.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { Form } from '@sola/ark-core/molecules/form/form.tsx';

import { DonateAmountSelector } from './components/donate-amount-selector.tsx';
import { DonateFormSkeleton } from './components/donate-form-skeleton.tsx';
import { DonateFrequencySelector } from './components/donate-frequency-selector.tsx';
import { DonateImpactText } from './components/donate-impact-text.tsx';
import { DonateSubmitButton } from './components/donate-submit-button.tsx';
import { useDonate } from './donate-context.tsx';
import { DonateFrequency } from './donate.types.ts';
import { useDonateActions } from './hooks/use-donate-actions.tsx';

export interface DonateFormProps {
  focusOnLoad?: boolean;
  alt?: boolean;
}
export const DonateForm: React.FC<DonateFormProps> = ({
  focusOnLoad = false,
  alt,
}) => {
  const { t } = useTranslation(['donations']);
  const { variant, state, paramsReady } = useDonate();
  const { handleSubmit } = useDonateActions();

  // While params resolve the selector group is replaced by an `aria-hidden`
  // skeleton, so screen readers would otherwise get no cue. Mark the wrapper
  // busy and label it so assistive tech announces the loading state.
  const isLoading = !alt && !paramsReady;

  return (
    <Form
      onSubmit={handleSubmit}
      options={{
        defaultValues: {
          donateFrequency: state.donateFrequency,
          [`${DonateFrequency.OneTime}-donateAmount`]:
            state.selectedAmounts[DonateFrequency.OneTime],
          [`${DonateFrequency.Monthly}-donateAmount`]:
            state.selectedAmounts[DonateFrequency.Monthly],
          [`${DonateFrequency.Annually}-donateAmount`]:
            state.selectedAmounts[DonateFrequency.Annually],
        },
      }}
    >
      <div
        className={`Donate Donate--${variant} flex flex-col ${
          variant === 'grid' ? 'gap-comfy-md' : 'gap-comfy-lg'
        }`}
        aria-busy={isLoading}
        aria-label={
          isLoading
            ? t('donations:loading', { defaultValue: 'Loading donation form' })
            : undefined
        }
      >
        {alt && (
          <>
            <div className="flex flex-row gap-comfy-sm">
              <DonateFrequencySelector alt={alt} />
              <DonateAmountSelector alt={alt} focusOnLoad={focusOnLoad} />
            </div>
            <DonateSubmitButton alt={alt} />
          </>
        )}
        {!alt && (
          <>
            {/*
              Gate the whole selector group on `paramsReady`. During the
              `DonateProvider` Suspense fallback the state is derived from the
              params-less `initialState`, so any selector rendered here (the
              frequency/amount selectors, and `DonateImpactText`, which derives
              its copy from `state.donateFrequency` / `getCurrentAmount()`)
              would show a pre-filter value that then changes once params
              resolve (a flash). Show the neutral skeleton instead until ready.
            */}
            {!paramsReady && <DonateFormSkeleton />}
            {paramsReady && (
              <>
                <DonateFrequencySelector />
                <DonateAmountSelector focusOnLoad={focusOnLoad} />
                <DonateImpactText />
              </>
            )}
            <DonateSubmitButton />
          </>
        )}
      </div>
    </Form>
  );
};
